import React, { useState, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableRow,
    TableHead, Paper, Button, TextField, TablePagination,
    Select, MenuItem, FormControl, InputLabel, Grid, Chip, Box,
    Tooltip, IconButton, Dialog, DialogTitle, DialogContent,
    DialogActions, CircularProgress, Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PrintIcon from '@mui/icons-material/Print';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';
import Swal from 'sweetalert2';

const ESTADOS = [
    { value: '', label: 'Todos' },
    { value: 'pendiente', label: 'Pendiente' },
    { value: 'en_lectura', label: 'En Lectura' },
    { value: 'informado', label: 'Informado' },
    { value: 'entregado', label: 'Entregado' },
];

function DicomWorklist() {
    const { tienePermiso } = useAccesos();
    const { user } = useUser();
    const server = process.env.REACT_APP_API_ROUTE;
    const navigate = useNavigate();

    const getPacienteNombre = (det) => {
        if (det.nombrePaciente) return det.nombrePaciente;
        if (det.paciente && typeof det.paciente === 'object') return det.paciente.nombreCompleto || '';
        if (typeof det.paciente === 'string') return det.paciente;
        return '';
    };
    const getPacienteIdentidad = (det) => {
        if (det.numeroIdentidad && typeof det.numeroIdentidad === 'string') return det.numeroIdentidad;
        if (det.paciente && typeof det.paciente === 'object') return det.paciente.numeroIdentidad || '';
        return '';
    };
    const getProductoNombre = (det) => {
        if (det.nombreProducto) return det.nombreProducto;
        if (det.producto && typeof det.producto === 'object') return det.producto.nombre || det.producto.descripcion || '';
        if (typeof det.producto === 'string') return det.producto;
        return '';
    };

    const getDefaultDesde = () => {
        const d = new Date();
        d.setMonth(d.getMonth() - 2);
        return d.toISOString().split('T')[0];
    };
    const getDefaultHasta = () => new Date().toISOString().split('T')[0];

    const sumarUnDia = (fechaYYYYMMDD) => {
        if (!fechaYYYYMMDD) return '';
        const [year, month, day] = fechaYYYYMMDD.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        date.setDate(date.getDate() + 1);

        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const [detalles, setDetalles] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filtroEstado, setFiltroEstado] = useState('');
    const [filtroBusqueda, setFiltroBusqueda] = useState('');
    const [filtroFechaDesde, setFiltroFechaDesde] = useState(getDefaultDesde());
    const [filtroFechaHasta, setFiltroFechaHasta] = useState(getDefaultHasta());
    const [loading, setLoading] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);

    const fetchWorklist = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            const fechaHastaMasUnDia = sumarUnDia(filtroFechaHasta);
            if (filtroEstado) params.append('estado', filtroEstado);
            if (filtroBusqueda) params.append('busqueda', filtroBusqueda);
            if (filtroFechaDesde) params.append('fechaDesde', filtroFechaDesde);
            // Enviamos fechaHasta + 1 dia para abarcar todo el dia seleccionado.
            if (fechaHastaMasUnDia) params.append('fechaHasta', fechaHastaMasUnDia);

            const response = await fetch(`${server}/MgOrdenesDeTrabajoDetalles/imagenes/worklist?${params.toString()}`);
            const data = await response.json();
            setDetalles(data.$values || data || []);
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', 'Error al cargar worklist', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (tienePermiso('Ver Worklist Estudios')) {
            fetchWorklist();
        }
    }, []);

    const handleBuscar = () => {
        setPage(0);
        fetchWorklist();
    };

    const handleCambiarEstado = async (detalle, nuevoEstado) => {
        if (!tienePermiso('Cambiar Estado Estudio')) return;
        if (!detalle.tieneEstudioDicom) {
            Swal.fire('Validación', 'No hay estudio DICOM asociado a este detalle', 'warning');
            return;
        }
        try {
            const response = await fetch(
                `${server}/ImgEstudiosDicom/${detalle.idEstudio}/estado?estado=${nuevoEstado}`,
                { method: 'PUT' }
            );
            if (response.ok) {
                Swal.fire('Éxito', `Estado cambiado a "${nuevoEstado}"`, 'success');
                fetchWorklist();
            } else {
                const errorData = await response.json();
                Swal.fire('Error', errorData.message || 'Error al cambiar estado', 'error');
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    const handleMarcarEntregado = async (detalle) => {
        if (!tienePermiso('Cambiar Estado Estudio')) return;
        const result = await Swal.fire({
            title: '¿Marcar como entregado?',
            text: `Paciente: ${getPacienteNombre(detalle)} — ${getProductoNombre(detalle)}`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, entregar',
            cancelButtonText: 'Cancelar'
        });
        if (!result.isConfirmed) return;
        try {
            const response = await fetch(
                `${server}/MgOrdenesDeTrabajoDetalles/${detalle.idDetalle}/estudio-entregado?usuario=${user.codigoUsuario.padEnd(20)}`,
                { method: 'PUT' }
            );
            if (response.ok) {
                Swal.fire('Éxito', 'Estudio marcado como entregado', 'success');
                fetchWorklist();
            } else {
                const errorData = await response.json();
                Swal.fire('Error', errorData.message || 'Error', 'error');
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    const handleAbrirVisor = (detalle) => {
        if (detalle.idEstudio) {
            navigate(`/ImgVisor?estudioId=${detalle.idEstudio}&detalleId=${detalle.idDetalle}`);
        } else {
            Swal.fire('Info', 'No hay estudio DICOM creado. Suba imágenes primero.', 'info');
        }
    };

    const handleAbrirUpload = (detalle) => {
        const params = new URLSearchParams({ detalleId: detalle.idDetalle });
        if (detalle.idEstudio) params.append('estudioId', detalle.idEstudio);
        navigate(`/ImgUpload?${params.toString()}`);
    };

    const handleAbrirInforme = (detalle) => {
        navigate(`/ImgInformes?detalleId=${detalle.idDetalle}`);
    };

    const handleAbrirEncuesta = (detalle) => {
        navigate(`/ImgEncuesta?detalleId=${detalle.idDetalle}`);
    };

    const handleCerrarPdf = () => {
        if (pdfUrl) {
            window.URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
        }
    };

    const handleImprimirInformeRayosX = async (detalle) => {
        const { value: incluirInforme } = await Swal.fire({
            title: 'Imprimir Informe Rayos X',
            text: `Paciente: ${getPacienteNombre(detalle)}`,
            icon: 'question',
            input: 'checkbox',
            inputValue: 1,
            inputPlaceholder: 'Incluir informe radiológico',
            showCancelButton: true,
            confirmButtonText: 'Imprimir',
            cancelButtonText: 'Cancelar'
        });
        if (incluirInforme === undefined) return; // cancelled
        setPdfLoading(true);
        setPdfUrl(null);
        try {
            const response = await fetch(
                `${server}/Whatsapp/ImprimirInformeRayosX/${detalle.idDetalle}?incluirInforme=${!!incluirInforme}`,
                { method: 'POST' }
            );
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                setPdfUrl(url);
            } else {
                const errorData = await response.json();
                Swal.fire('Error', errorData.message || 'Error al generar informe', 'error');
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        } finally {
            setPdfLoading(false);
        }
    };

    const handleEnviarWhatsApp = async (detalle) => {
        const { value: formValues } = await Swal.fire({
            title: 'Enviar por WhatsApp',
            html:
                `<p style="margin-bottom:8px"><b>Paciente:</b> ${getPacienteNombre(detalle)}</p>` +
                '<input id="swal-input-tel" class="swal2-input" placeholder="Número WhatsApp (ej: 50499887766)" type="tel">' +
                '<label style="display:flex;align-items:center;gap:6px;margin-top:8px;justify-content:center">' +
                '<input id="swal-input-informe" type="checkbox" checked> Incluir informe radiológico</label>',
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Enviar',
            cancelButtonText: 'Cancelar',
            preConfirm: () => {
                const numero = document.getElementById('swal-input-tel').value.trim();
                if (!numero) {
                    Swal.showValidationMessage('Ingrese el número de WhatsApp');
                    return false;
                }
                return {
                    numero,
                    incluirInforme: document.getElementById('swal-input-informe').checked
                };
            }
        });
        if (!formValues) return;
        try {
            Swal.fire({ title: 'Enviando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            const params = new URLSearchParams({
                idDetalleOrdenTrabajo: detalle.idDetalle,
                numeroWhatsapp: formValues.numero,
                incluirInforme: formValues.incluirInforme
            });
            const response = await fetch(
                `${server}/Whatsapp/PostInformeRayosXWhatsapp?${params.toString()}`,
                { method: 'POST' }
            );
            if (response.ok) {
                Swal.fire('Éxito', 'Informe enviado por WhatsApp', 'success');
            } else {
                const errorData = await response.json();
                Swal.fire('Error', errorData.message || 'Error al enviar por WhatsApp', 'error');
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    const getEstadoChip = (estadoFlujo) => {
        const colores = {
            pendiente: 'warning',
            en_lectura: 'info',
            informado: 'success',
            entregado: 'default',
        };
        return <Chip label={estadoFlujo || 'pendiente'} color={colores[estadoFlujo] || 'default'} size="small" />;
    };

    return (
        <div className="right_col" role="main">
        <div className="x_content">
            <div className="x_title">
                <h2>Worklist — Estudios de Imagen</h2>
            </div>

            {/* Filtros */}
            <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Buscar (paciente, orden, identidad)"
                            value={filtroBusqueda}
                            onChange={(e) => setFiltroBusqueda(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleBuscar()}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth>
                            <InputLabel>Estado</InputLabel>
                            <Select
                                value={filtroEstado}
                                onChange={(e) => setFiltroEstado(e.target.value)}
                                label="Estado"
                            >
                                {ESTADOS.map(e => (
                                    <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Desde"
                            value={filtroFechaDesde}
                            onChange={(e) => setFiltroFechaDesde(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Hasta"
                            value={filtroFechaHasta}
                            onChange={(e) => setFiltroFechaHasta(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Button
                            variant="contained"
                            onClick={handleBuscar}
                            disabled={loading}
                        >
                            {loading ? 'Buscando...' : 'Buscar'}
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            {/* Tabla */}
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Detalle</TableCell>
                            <TableCell>Paciente</TableCell>
                            <TableCell>Identidad</TableCell>
                            <TableCell>Producto/Examen</TableCell>
                            <TableCell align="center">Imagen</TableCell>
                            <TableCell>Fecha</TableCell>
                            <TableCell align="center">Estado</TableCell>
                            <TableCell align="center">DICOM</TableCell>
                            <TableCell align="center">Informe</TableCell>
                            <TableCell align="center">Encuesta</TableCell>
                            <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {detalles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((det) => (
                            <TableRow
                                key={det.idDetalle}
                                hover
                                onDoubleClick={() => handleAbrirVisor(det)}
                                style={{ cursor: 'pointer' }}
                            >
                                <TableCell>{det.abreviaturaSucursal || ''}{det.ordenMedicaMes || det.idDetalle}</TableCell>
                                <TableCell>{getPacienteNombre(det)}</TableCell>
                                <TableCell>{getPacienteIdentidad(det)}</TableCell>
                                <TableCell>{getProductoNombre(det)}</TableCell>
                                <TableCell align="center">
                                    {det.producto && det.producto.imagenUrl ? (
                                        <Tooltip title={`${getProductoNombre(det)} - Click para ver`}>
                                            <Box
                                                component="img"
                                                src={det.producto.imagenUrl}
                                                alt={getProductoNombre(det)}
                                                sx={{
                                                    maxWidth: '50px',
                                                    maxHeight: '50px',
                                                    borderRadius: 1,
                                                    cursor: 'pointer',
                                                    '&:hover': { opacity: 0.8, transform: 'scale(1.05)' }
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    window.open(det.producto.imagenUrl, '_blank');
                                                }}
                                            />
                                        </Tooltip>
                                    ) : (
                                        <Chip label="—" size="small" variant="outlined" />
                                    )}
                                </TableCell>
                                <TableCell>
                                    {det.fechaOrden
                                        ? new Date(det.fechaOrden).toLocaleDateString()
                                        : ''}
                                </TableCell>
                                <TableCell align="center">{getEstadoChip(det.estadoFlujo)}</TableCell>
                                <TableCell align="center">
                                    {det.tieneEstudioDicom ? (
                                        <Tooltip title={`${det.numeroImagenesDicom || 0} imagen(es)`}>
                                            <Chip label={det.numeroImagenesDicom || 0} size="small" color="primary" variant="outlined" />
                                        </Tooltip>
                                    ) : (
                                        <Chip label="Sin" size="small" variant="outlined" />
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    {det.tieneInforme ? (
                                        det.informeFirmado
                                            ? <Chip icon={<CheckCircleIcon />} label="Firmado" size="small" color="success" variant="outlined" />
                                            : <Chip label="Borrador" size="small" color="warning" variant="outlined" />
                                    ) : (
                                        <Chip label="Sin" size="small" variant="outlined" />
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    {det.tieneEncuesta
                                        ? <Chip label="Sí" size="small" color="success" variant="outlined" />
                                        : <Chip label="No" size="small" variant="outlined" />
                                    }
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    <Tooltip title="Subir imágenes">
                                        <span>
                                        <IconButton
                                            size="small"
                                            color="secondary"
                                            onClick={() => handleAbrirUpload(det)}
                                            disabled={!tienePermiso('Subir Archivo DICOM')}
                                        >
                                            <CloudUploadIcon fontSize="small" />
                                        </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Visor DICOM">
                                        <span>
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => handleAbrirVisor(det)}
                                            disabled={!tienePermiso('Ver Visor DICOM') || !det.tieneEstudioDicom}
                                        >
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Encuesta">
                                        <span>
                                        <IconButton
                                            size="small"
                                            color="info"
                                            onClick={() => handleAbrirEncuesta(det)}
                                            disabled={!tienePermiso('Ver Encuesta IMG')}
                                        >
                                            <AssignmentIcon fontSize="small" />
                                        </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Informe">
                                        <span>
                                        <IconButton
                                            size="small"
                                            color="warning"
                                            onClick={() => handleAbrirInforme(det)}
                                            disabled={!tienePermiso('Crear Informe Radiologico')}
                                        >
                                            <DescriptionIcon fontSize="small" />
                                        </IconButton>
                                        </span>
                                    </Tooltip>
                                    {det.tieneEstudioDicom && (
                                        <Tooltip title="Imprimir Informe Rayos X">
                                            <span>
                                            <IconButton
                                                size="small"
                                                color="default"
                                                onClick={() => handleImprimirInformeRayosX(det)}
                                                disabled={!tienePermiso('Imprimir Informe Radiologico')}
                                            >
                                                <PrintIcon fontSize="small" />
                                            </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                    {det.tieneEstudioDicom && (
                                        <Tooltip title="Enviar por WhatsApp">
                                            <span>
                                            <IconButton
                                                size="small"
                                                color="success"
                                                onClick={() => handleEnviarWhatsApp(det)}
                                                disabled={!tienePermiso('Imprimir Informe Radiologico')}
                                            >
                                                <WhatsAppIcon fontSize="small" />
                                            </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                    {det.estadoFlujo === 'pendiente' && det.tieneEstudioDicom && (
                                        <Tooltip title="Pasar a En Lectura">
                                            <span>
                                            <Button
                                                size="small"
                                                color="info"
                                                onClick={() => handleCambiarEstado(det, 'en_lectura')}
                                                disabled={!tienePermiso('Cambiar Estado Estudio')}
                                                sx={{ minWidth: 'auto', fontSize: 11 }}
                                            >
                                                → Lectura
                                            </Button>
                                            </span>
                                        </Tooltip>
                                    )}
                                    {det.estadoFlujo === 'informado' && (
                                        <Tooltip title="Marcar Entregado">
                                            <span>
                                            <IconButton
                                                size="small"
                                                color="default"
                                                onClick={() => handleMarcarEntregado(det)}
                                                disabled={!tienePermiso('Cambiar Estado Estudio')}
                                            >
                                                <LocalShippingIcon fontSize="small" />
                                            </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                        {detalles.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={10} align="center">
                                    {loading ? 'Cargando...' : 'No se encontraron detalles de imagen'}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={detalles.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </TableContainer>

            {/* Modal PDF Preview */}
            <Dialog
                open={pdfLoading || !!pdfUrl}
                onClose={handleCerrarPdf}
                fullScreen
                PaperProps={{ style: { display: 'flex', flexDirection: 'column', height: '100vh', margin: 0 } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Vista Previa — Informe Rayos X
                    <IconButton onClick={handleCerrarPdf}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent style={{ padding: 0, display: 'flex', flex: 1, overflow: 'hidden' }}>
                    {pdfLoading && (
                        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1}>
                            <CircularProgress size={60} />
                            <Typography variant="body1" sx={{ mt: 2 }}>Generando informe...</Typography>
                        </Box>
                    )}
                    {pdfUrl && !pdfLoading && (
                        <iframe
                            src={pdfUrl}
                            style={{ width: '100%', height: '100%', border: 'none', flex: 1 }}
                            title="Vista previa del informe radiológico"
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCerrarPdf} variant="outlined" color="secondary">Cerrar</Button>
                </DialogActions>
            </Dialog>
        </div>
        </div>
    );
}

export default DicomWorklist;
